import { useRef } from 'react'
import { FileUp, FileText } from 'lucide-react'
import { extractTextFromPdf } from '@/lib/pdf/extractor'

interface DocumentTabProps {
  content: string
  onContentChange: (content: string) => void
  file: File | null
  onFileChange: (file: File | null) => void
  onTitleSuggestion: (title: string) => void
  disabled: boolean
}

export function DocumentTab({
  content,
  onContentChange,
  file,
  onFileChange,
  onTitleSuggestion,
  disabled,
}: DocumentTabProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (!selected) return

    onFileChange(selected)
    onTitleSuggestion(selected.name.replace(/\.[^.]+$/, ''))

    if (selected.name.toLowerCase().endsWith('.pdf')) {
      try {
        const result = await extractTextFromPdf(selected)
        onContentChange(result.fullText)
      } catch {
        onContentChange('')
      }
    } else {
      const text = await selected.text()
      onContentChange(text)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const dropped = e.dataTransfer.files[0]
    if (dropped) {
      const fakeEvent = { target: { files: [dropped] } } as unknown as React.ChangeEvent<HTMLInputElement>
      handleFileSelect(fakeEvent)
    }
  }

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".txt,.md,.markdown,.pdf"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />
      {file ? (
        <div className="flex items-center gap-3 p-3 bg-warm-50 border border-warm-200 rounded-lg">
          <FileText className="w-5 h-5 text-solution flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{file.name}</div>
            <div className="text-xs text-text-muted">
              {(file.size / 1024).toFixed(1)} KB
              {content.length > 0 && ` · ${content.length} characters`}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              onFileChange(null)
              onContentChange('')
            }}
            className="text-xs text-text-muted hover:text-text-secondary"
            disabled={disabled}
          >
            Remove
          </button>
        </div>
      ) : (
        <div
          className={`border-2 border-dashed border-warm-200 rounded-lg p-8 text-center transition-colors ${
            disabled ? 'opacity-50' : 'cursor-pointer hover:border-warm-400'
          }`}
          onClick={() => !disabled && fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <FileUp className="w-7 h-7 text-warm-400 mx-auto mb-2" />
          <p className="text-sm text-text-secondary">Click or drop a file</p>
          <p className="text-xs text-text-muted mt-1">PDF, TXT, MD</p>
        </div>
      )}
    </div>
  )
}
